// M004-F0748 (WebUI #188, gate corrected in WebUI #203). Pure rules of the reviewer surface, unit-tested directly (T-M004-ENVUX-019). Nothing is
// shown that the Ledger did not send; the permission is DATA, never a role-name branch.

// The tab is offered only to holders of the organisation's `ledger_manage` permission: the key Core returns from my-permissions and the one Core's
// reviewer read route requires (Core main src/services/serviceLinkPassthrough/cslptManifest.ts:131 ENVELOPE_REVIEW_READ permission_any ['ledger_manage']).
// `expense_claims_review` is NOT a Core permission key, so gating on it hid the tab from everyone. Fail closed on anything but literal true.
export const canReviewClaims = (permissions) => permissions?.ledger_manage === true;

export const STATE_TABS = Object.freeze([
  { key: 'submitted', label: 'Submitted' },
  { key: 'returned', label: 'Returned' },
  { key: 'approved', label: 'Approved' },
  { key: 'all', label: 'All' },
]);
export const STATE_TEXT = Object.freeze({ submitted: 'Submitted', returned: 'Returned', approved: 'Approved', reimbursed: 'Reimbursed' });
export const stateText = (state) => STATE_TEXT[state] || String(state || 'Unknown');

export const SELF_APPROVAL_REASON = 'You cannot approve your own claim - another reviewer must.';
export const NOT_SUBMITTED_REASON = 'Only a submitted claim can be approved.';
export const REASON_REQUIRED_TEXT = 'Say why you are returning this claim; the claimant sees it.';

// Approve all: disabled with its reason on the reviewer's own claim (maker-checker) and on any claim that is not awaiting review.
// The Ledger still refuses a forced self-approval (ENVELOPE_CLAIM_SELF_APPROVAL_DENIED); the UI disables first.
export function approveDecision(claim) {
  if (!claim) return { disabled: true, reason: NOT_SUBMITTED_REASON };
  if (claim.own_claim === true) return { disabled: true, reason: SELF_APPROVAL_REASON };
  if (claim.state !== 'submitted') return { disabled: true, reason: NOT_SUBMITTED_REASON };
  return { disabled: false, reason: '' };
}

// Return all: allowed on one's own claim (a return posts no money); needs a reason; only a submitted claim can be returned.
export const cleanReason = (text) => String(text ?? '').trim();
export function returnDecision(claim, reason) {
  if (!claim || claim.state !== 'submitted') return { disabled: true, reason: 'Only a submitted claim can be returned.' };
  if (!cleanReason(reason)) return { disabled: true, reason: REASON_REQUIRED_TEXT };
  return { disabled: false, reason: '' };
}

export const money = (minor, currency = 'GBP') => new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(Number(minor || 0) / 100);
export const formatDay = (iso) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '-';
};

// Core's closed error envelope, or a thrown error: the sentence and the code are both shown (design section 9).
export function refusalInfo(cause) {
  const body = cause?.response?.data;
  const code = (typeof body?.error === 'string' ? body.error : body?.error?.code) || body?.code || '';
  const message = body?.message || body?.error?.message || cause?.message || 'The Ledger could not do that.';
  return { code, message, status: cause?.response?.status, denied: code === 'ENVELOPE_CLAIM_REVIEW_DENIED' };
}

export const queueOf = (response) => {
  const claims = response?.data?.data?.claims;
  if (!Array.isArray(claims)) throw new Error('The Ledger answered with a claim queue this page cannot read.');
  return claims;
};

// Rows in a state tab; "All" keeps the Ledger's order (newest first).
export const inState = (claims, key) => (key === 'all' ? claims : claims.filter((claim) => claim.state === key));
export const submittedCount = (claims) => claims.filter((claim) => claim.state === 'submitted').length;
